/**
 * 見積依頼の送付の画面 /rfqs/:id/send（2026-10-21 射場さんの決め＝docs/89 ★16・§17.5、docs/88 §16）。見積書・請求書の紙の画面と同じ形：
 *   左＝送付状の見本（オンのとき）と見積依頼書の紙（どちらも透かし「確認用」。番号は送る瞬間に採るので付いていない）と同封書類の紙、
 *   右＝送付状の枠（components/CoverLetterPanel.tsx。件名・題・挨拶・本文・備考・記・窓付封筒・同じ送付先の書類を追加）
 *   ?via=print|email・cover=fax|window|off（見積依頼書の画面の送信設定の帯）・bcc=0|1（メールの控え）
 *   印刷・FAX：主「送付済にして印刷」＝番号を採って送付済（POST /rfqs/:id/issue）→ 送付状がオンなら送付状が1枚目の透かしの無い PDF 1本を作って記録に固める
 *     （POST /deliveries）→ その PDF を印刷の画面へ（オフなら送った見積依頼書の PDF）。脇「PDF 保存」（同じく番号を採って PDF を保存）・「取消」（番号を採らずに戻る）。
 *     印刷の画面を閉じたら見積依頼書の画面へ戻る（afterprint）
 *   メール（送付状がオンのときだけここへ来る）：右の枠の下に宛先（アドレスが無いとき）と「メール本文 ⌄」。主「送信」＝POST /rfqs/:id/send（送付状の中身つき）。
 *     メールは見積依頼書と送付状だけ（同じ送付先の書類を追加は出さない）。送ったあとの依頼の「メールで送信…」（もう一度送る）もここ（番号は採り直さない）
 *   送ったあとの依頼を印刷・FAX で開いたときは紙の画面（/rfqs/:id/paper＝送付状の枠と束ね）へ移す。PC だけ（PC_ONLY_FEATURES の「見積依頼の送付」）
 */
import { COVER_LETTER_MSG, MSG, RFQ_MSG, RFQ_STATUS_LABEL, RfqSendRequest, coverItemKey, type DeliveryItemRef, type DeliveryResult, type Rfq } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { api, fetchFile, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { CoverBundlePicker, useItemPages } from "../../components/CoverBundle.jsx";
import { CoverLetterPanel, contentOf, useCoverLetter } from "../../components/CoverLetterPanel.jsx";
import { ErrorText, Field, useToast } from "../../components/ui.jsx";
import { downloadBlob, portraitForPrint, renderPdf, type PageImg } from "../../lib/pdfPages.js";

export function RfqSendPage() {
  const { id = "" } = useParams();
  const [sp] = useSearchParams();
  const via: "print" | "email" = sp.get("via") === "email" ? "email" : "print";
  const coverParam = sp.get("cover");
  const bcc = sp.get("bcc") !== "0";
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const back = useBackNav({ to: `/rfqs/${id}`, label: RFQ_MSG.title });
  usePageName(RFQ_MSG.sendPageTitle);
  const q = useQuery({ queryKey: ["rfq", id], queryFn: () => api<Rfq>("GET", `/rfqs/${id}`), retry: false });
  const r = q.data;
  const draft = r?.status === "draft";
  // 同封書類（見積依頼書が先頭・印刷・FAX で「同じ送付先の書類を追加」したもの）
  const [extra, setExtra] = useState<DeliveryItemRef[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const rfqRef: DeliveryItemRef = { kind: "rfq", id };
  const refs = useMemo(() => [rfqRef, ...extra], [id, extra]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = useMemo(() => refs.map((x): DeliveryItemRef => ((x.kind === "received" || x.kind === "file") && labels[coverItemKey(x)]?.trim() ? { ...x, label: labels[coverItemKey(x)]!.trim() } : x)), [refs, labels]);
  const target = r && (draft || via === "email") ? { items } : null;
  const cover = useCoverLetter(target, { defaultOn: coverParam !== "off", defaultWindow: coverParam === "window", draftRfq: draft, watermark: draft ? "confirm" : undefined });
  const itemPages = useItemPages(extra);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  // メール：宛先（アドレスが無いときだけ欄）・件名・本文（「メール本文 ⌄」で開く）
  const known = r?.contact?.email ?? r?.mail?.to ?? r?.contacts.find((c) => c.email)?.email ?? "";
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [mailOpen, setMailOpen] = useState(false);
  useEffect(() => {
    if (!r) return;
    setTo((v) => v || known);
    setSubject((v) => v || (r.mail?.subject ?? ""));
    setBody((v) => v || (r.mail?.body ?? ""));
  }, [r?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 見積依頼書の紙（下書きは透かし「確認用」＝?wm=confirm。送ったあとは保存した PDF）
  const [pages, setPages] = useState<PageImg[]>([]);
  const [paperErr, setPaperErr] = useState<string | undefined>();
  const pdfUrl = r ? `/api/v1/rfqs/${id}/pdf?${draft ? "wm=confirm&" : ""}v=${encodeURIComponent(r.updatedAt)}` : null;
  useEffect(() => {
    if (!pdfUrl) return;
    let alive = true;
    setPages([]);
    setPaperErr(undefined);
    void (async () => {
      try {
        const blob = await fetchFile(pdfUrl);
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        if (alive) setPages(got);
      } catch (e) {
        if (alive) setPaperErr(messageOf(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [pdfUrl]);
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  // 印刷：描き終えてから刷る。印刷の画面を閉じたら見積依頼書の画面へ（送付済になっている）
  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  const after = useRef<string | null>(null);
  /**
   * 番号を採った依頼（送付済）。画面のデータ（["rfq", id]）は流れが終わるまで下書きのまま置く＝途中で送付済に入れ替えると、
   * 「送ったあとの依頼は紙の画面へ」の決まりで印刷の前に画面が移ってしまう（2026-10-21 の確かめで見つけた）
   */
  const issued = useRef<Rfq | null>(null);
  /** 開いた時点で送付済だったか（null＝まだ読んでいない） */
  const openedSent = useRef<boolean | null>(null);
  useEffect(() => {
    if (!printPages) return;
    const onAfter = () => {
      if (!after.current) return;
      nav(`/rfqs/${id}`, { replace: true, state: { notice: after.current } });
      if (issued.current) refresh(issued.current);
    };
    window.addEventListener("afterprint", onAfter, { once: true });
    const f = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => {
      cancelAnimationFrame(f);
      window.removeEventListener("afterprint", onAfter);
    };
  }, [printPages]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  // 送ったあとの依頼の印刷・FAX は紙の画面（送付状の枠・束ねがある）。判定は開いた時点の状態だけ（この画面で送付済にしたあとは、
  // 印刷を終えて見積依頼書の画面へ戻るまで紙の画面へ移さない）
  if (r && openedSent.current === null) openedSent.current = !draft;
  if (openedSent.current && via === "print") return <Navigate to={`/rfqs/${id}/paper`} replace />;

  const refresh = (v?: Rfq) => {
    if (v) qc.setQueryData(["rfq", id], v);
    void qc.invalidateQueries({ queryKey: ["rfqs"] });
    void qc.invalidateQueries({ queryKey: ["rfq", id] });
    void qc.invalidateQueries({ queryKey: ["deliveries"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    if (r?.estimate) void qc.invalidateQueries({ queryKey: ["estimate-rfqs", r.estimate.id] });
  };
  /**
   * 送付済にする（番号を採る）→ 送付状がオンなら送付状が1枚目の PDF 1本を作って記録に固める・オフなら送った見積依頼書の PDF。
   * 送付状の中身は送る前に作った見本と同じ（記の行の番号は送ったあとの番号）
   */
  const issueAndPdf = async (method: "print" | "download"): Promise<{ blob: Blob; fileName: string; no: string }> => {
    const v = await api<Rfq>("POST", `/rfqs/${id}/issue`, { via: "print" });
    issued.current = v;
    if (cover.on && cover.form) {
      const d = await api<DeliveryResult>("POST", "/deliveries", { method, target: { items }, cover: contentOf(cover.form) });
      return { blob: await fetchFile(d.pdfUrl), fileName: d.fileName, no: v.no ?? "" };
    }
    return { blob: await fetchFile(`/api/v1/rfqs/${id}/pdf`), fileName: `${RFQ_MSG.title}_${v.no ?? ""}_${v.vendor.shortName}.pdf`.replace(/[\\/:*?"<>|\s]+/g, "_"), no: v.no ?? "" };
  };
  const doPrint = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const out = await issueAndPdf("print");
      const got: PageImg[] = [];
      await renderPdf(await out.blob.arrayBuffer(), (p, i) => {
        got[i - 1] = p;
      });
      after.current = RFQ_MSG.issued.replace("{no}", out.no);
      toast(after.current);
      setPrintPages(await portraitForPrint(got));
    } catch (e) {
      afterIssueFailed(e);
    } finally {
      setBusy(false);
    }
  };
  /** 番号を採ったあとで断られた（送付状の PDF が作れない など）：送付済のまま、送ったあとの依頼の紙の画面（送付状の枠と束ね）で刷り直せる */
  const afterIssueFailed = (e: unknown) => {
    const v = issued.current;
    if (!v) return setErr(messageOf(e));
    nav(`/rfqs/${id}/paper`, { replace: true, state: { notice: messageOf(e) } });
    refresh(v);
  };
  const doSave = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const out = await issueAndPdf("download");
      downloadBlob(out.blob, out.fileName);
      nav(`/rfqs/${id}`, { replace: true, state: { notice: RFQ_MSG.issued.replace("{no}", out.no) } });
      if (issued.current) refresh(issued.current);
    } catch (e) {
      afterIssueFailed(e);
      setBusy(false);
    }
  };
  const doMail = async () => {
    const v = RfqSendRequest.safeParse({ to, bccSelf: bcc, subject, body, mailMode: r?.mail?.mode ?? "attach", ...(cover.on && cover.form ? { cover: contentOf(cover.form) } : {}) });
    if (!v.success) {
      setErr(v.error.issues[0]?.message);
      if (v.error.issues.some((i) => i.path[0] === "subject" || i.path[0] === "body")) setMailOpen(true);
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const sent = await api<Rfq>("POST", `/rfqs/${id}/send`, v.data);
      refresh(sent);
      nav(`/rfqs/${id}`, { replace: true, state: { notice: RFQ_MSG.sent.replace("{no}", sent.no ?? "") } });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };

  const ready = !!r && cover.ready && !!pages.length && !busy && !cover.bundleError;
  const bundle = {
    labels,
    onLabel: (key: string, v: string) => setLabels((ls) => ({ ...ls, [key]: v })),
    onMove: (i: number, dir: -1 | 1) => {
      const next = refs.slice();
      const j = i + dir;
      if (j < 0 || j >= next.length) return;
      [next[i], next[j]] = [next[j]!, next[i]!];
      // 見積依頼書は先頭のまま（並べ替えは同封書類どうし）
      if (next[0]!.kind !== "rfq") return;
      setExtra(next.slice(1));
    },
    onRemove: (i: number) => setExtra(refs.filter((_, j) => j !== i).slice(1)),
    fixed: new Set([coverItemKey(rfqRef)]),
  };
  const who = r ? (r.contact ? `${r.vendor.shortName}　${[r.contact.department, r.contact.name].filter(Boolean).join(" ")} 様` : `${r.vendor.shortName} 御中`) : "";
  return (
    <div className="paperview rqsendpage" data-testid="rfq-send-page">
      <style>{"@page { size: A4; margin: 0; }"}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        <h1>{RFQ_MSG.sendPageTitle}</h1>
        {r ? (
          <span className="chip" data-status={r.status} data-testid="rfq-send-status">
            {RFQ_STATUS_LABEL[r.status]}
          </span>
        ) : null}
        <span className="hint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r ? `${who}　${r.subject}` : MSG.pdfMaking}
        </span>
        <span className="sp" />
        <button type="button" className="btn" disabled={busy} onClick={back.request} data-testid="rfq-send-cancel">
          取消
        </button>
        {via === "print" ? (
          <>
            <button type="button" className="btn" disabled={!ready} title={RFQ_MSG.printSaveTitle} onClick={() => void doSave()} data-testid="rfq-send-do-save">
              {RFQ_MSG.printSave}
            </button>
            <button type="button" className="btn primary" disabled={!ready} onClick={() => void doPrint()} data-testid="rfq-send-do-print">
              {busy ? COVER_LETTER_MSG.making : RFQ_MSG.printDo}
            </button>
          </>
        ) : (
          <button type="button" className="btn primary" disabled={!ready || !to} onClick={() => void doMail()} data-testid="rfq-send-do-mail">
            {busy ? RFQ_MSG.sending : RFQ_MSG.sendDo}
          </button>
        )}
      </div>
      <ErrorText>{err ?? paperErr}</ErrorText>
      <div className="paperwrap">
        <div className="pages" aria-label={`${RFQ_MSG.title}の紙`}>
          {cover.on
            ? cover.pages.map((p, i) => (
                <div className="clsheet" key={`c${i}`} data-testid="cover-sheet">
                  <img src={p.url} alt={`送付状 ${i + 1}ページ目`} width={p.w} height={p.h} />
                </div>
              ))
            : null}
          {pages.map((p, i) => (
            <div className="paperpage" key={i} data-testid="rfq-send-sheet">
              <img src={p.url} alt={`${RFQ_MSG.title} ${i + 1}ページ目`} width={p.w} height={p.h} />
            </div>
          ))}
          {!pages.length && !paperErr ? (
            <p className="hint" role="status">
              {MSG.pdfMaking}
            </p>
          ) : null}
          {extra.map((x, i) => {
            const qx = itemPages[i];
            const it = cover.draft.data?.items.find((y) => y.key === coverItemKey(x));
            const name = it ? `${it.label}${it.docNo ? ` ${it.docNo}` : ""}` : COVER_LETTER_MSG.bundleTitle;
            return (
              <div className="clitem" key={coverItemKey(x)} data-testid="cover-item-pages">
                {qx?.isError ? <ErrorText>{messageOf(qx.error)}</ErrorText> : null}
                {qx?.data
                  ? qx.data.map((p, j) => (
                      <div className="clsheet clitempage" key={j} style={p.w > p.h ? { width: "min(100%, 1160px)" } : undefined}>
                        <img src={p.url} alt={`${name} ${j + 1}ページ目`} width={p.w} height={p.h} />
                      </div>
                    ))
                  : null}
              </div>
            );
          })}
        </div>
        <div className="rqsend-side">
          <CoverLetterPanel
            c={cover}
            collapsible={false}
            bundle={extra.length ? bundle : undefined}
            // 同じ送付先の書類を追加は印刷・FAX だけ（メールは見積依頼書と送付状だけ）。選んだ書類はこの画面の同封書類に入る
            onAddDocs={via === "print" ? () => setPicking(true) : undefined}
            addDocsLabel={COVER_LETTER_MSG.addSameRecipient}
          />
          {via === "email" ? (
            <section className="clpanel rqsend-mail" aria-label={RFQ_MSG.mailBodyToggle} data-testid="rfq-send-mailbox">
              {!known ? (
                <Field id="rqs_to" label={RFQ_MSG.mailTo}>
                  <input id="rqs_to" className="inp" type="email" autoComplete="off" placeholder=" " value={to} maxLength={254} onChange={(e) => setTo(e.target.value.trim())} data-testid="rfq-to" />
                </Field>
              ) : (
                <p className="hint" data-testid="rfq-send-to">{`${RFQ_MSG.sendTo}　${known}`}</p>
              )}
              <button type="button" className="btn text rqsend-toggle" aria-expanded={mailOpen} aria-controls="rqs-mail" onClick={() => setMailOpen(!mailOpen)} data-testid="rfq-mail-toggle">
                {RFQ_MSG.mailBodyToggle} <span className={"vqd-chev" + (mailOpen ? " is-open" : "")} aria-hidden="true" />
              </button>
              {mailOpen ? (
                <div id="rqs-mail">
                  <Field id="rqs_msub" label={RFQ_MSG.mailSubject}>
                    <input id="rqs_msub" className="inp" placeholder=" " value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} data-testid="rfq-mail-subject" />
                  </Field>
                  <Field id="rqs_mbody" label={RFQ_MSG.mailBody}>
                    <textarea id="rqs_mbody" className="inp" rows={8} placeholder=" " value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)} data-testid="rfq-mail-body" />
                  </Field>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      </div>
      {picking && target ? (
        <CoverBundlePicker
          target={target}
          onClose={() => setPicking(false)}
          onPick={(picked, ls) => {
            setPicking(false);
            if (Object.keys(ls).length) setLabels((cur) => ({ ...cur, ...ls }));
            setExtra((cur) => [...cur, ...picked]);
          }}
        />
      ) : null}
      {back.dialog}
      {toastNode}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(printPages ?? []).map((p, i) => (
            <div className="printedPage" key={i}>
              <img src={p.url} alt="" width={p.w} height={p.h} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
